import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "fri_pwa_install_dismissed_at";
const REMIND_AFTER_DAYS = 7; // re-show the banner one week after it was dismissed

/**
 * Persistent install-prompt banner rendered above the candidate dashboard.
 *
 * Behavior:
 *  - Auto-hides if the app is already running as an installed PWA (display-mode:standalone or iOS standalone).
 *  - Hides if the browser has not yet fired `beforeinstallprompt` (Firefox / older iOS Safari / already installed).
 *  - Hides if the user dismissed it in the last 7 days (localStorage timestamp).
 *  - After install (`appinstalled`), removed permanently and dismiss key set.
 */
export default function PWAInstallBanner({ testId = "pwa-install-banner" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Already installed / standalone?
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) { setInstalled(true); return; }

    // Check dismissal timestamp
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (!Number.isNaN(ts) && Date.now() - ts < REMIND_AFTER_DAYS * 86_400_000) {
          setDismissed(true);
        }
      }
    } catch { /* localStorage unavailable — ignore */ }

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  if (installed || dismissed || !deferredPrompt) return null;

  const handleInstall = async () => {
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setInstalled(true);
      }
    } finally {
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  return (
    <div
      className="relative bg-gradient-to-r from-[#662483] to-[#0069B4] text-white"
      data-testid={testId}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3 flex-wrap">
        <div className="w-9 h-9 bg-white/15 flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold leading-tight">Installez FR INTERIM sur votre appareil</p>
          <p className="text-xs opacity-90 mt-0.5">
            Accédez plus vite à vos missions, offres et acomptes — comme une vraie application.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={handleInstall}
            className="bg-[#E5CD96] text-[#3F3F3F] hover:bg-[#B8A478] font-semibold rounded-none h-9 text-sm"
            data-testid={`${testId}-install`}
          >
            <Download className="w-4 h-4 mr-1.5" /> Installer
          </Button>
          <button
            onClick={handleDismiss}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-none"
            aria-label="Masquer"
            title="Masquer pendant 7 jours"
            data-testid={`${testId}-dismiss`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
